import React from 'react';
import { Clock, Hash, DollarSign, CheckCircle, AlertCircle } from 'lucide-react';

const AiDrillProgressModal = ({ isOpen, progress, metadata }) => {
  if (!isOpen) return null;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCost = (cost) => {
    return `${cost.toFixed(4)} CNY`;
  };

  const getStepStatus = (stepIndex) => {
    if (progress.currentStep > stepIndex) return 'completed';
    if (progress.currentStep === stepIndex) return 'active';
    return 'pending';
  };

  const steps = [
    '分析原始題目結構',
    '生成相似題目模式',
    '創建 5 道練習題',
    '生成詳細解答',
    '驗證題目完整性'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        {/* Header */}
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            AI 練習題生成中
          </h3>
          <p className="text-gray-600 text-sm">
            正在分析題目並生成相似練習題
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>開始生成</span>
            <span>完成</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress.percentage}%` }}
            ></div>
          </div>
          <div className="text-center mt-2">
            <span className="text-lg font-bold text-purple-600">
              {progress.percentage}%
            </span>
          </div>
        </div>

        {/* Time and Token Info */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-gray-600">
              <Clock className="w-4 h-4 mr-2" />
              <span className="text-sm">已用時間</span>
            </div>
            <span className="font-medium">
              {formatTime(Math.floor(progress.elapsedTime / 1000))}
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center text-gray-600">
              <Hash className="w-4 h-4 mr-2" />
              <span className="text-sm">已用 Token</span>
            </div>
            <span className="font-medium">
              {metadata?.tokensUsed || 0} / {metadata?.estimatedTokens || '2.3k'} 預估
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center text-gray-600">
              <DollarSign className="w-4 h-4 mr-2" />
              <span className="text-sm">費用</span>
            </div>
            <span className="font-medium">
              {metadata?.cost ? formatCost(metadata.cost.totalCost) : '計算中...'}
            </span>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-3 mb-6">
          {steps.map((step, index) => {
            const status = getStepStatus(index);
            return (
              <div key={index} className="flex items-center">
                <div className={`w-4 h-4 rounded-full mr-3 flex items-center justify-center ${
                  status === 'completed' ? 'bg-green-500' :
                  status === 'active' ? 'bg-orange-500' : 'bg-gray-300'
                }`}>
                  {status === 'completed' && <CheckCircle className="w-3 h-3 text-white" />}
                  {status === 'active' && <AlertCircle className="w-3 h-3 text-white" />}
                </div>
                <span className={`text-sm ${
                  status === 'completed' ? 'text-green-600' :
                  status === 'active' ? 'text-orange-600' : 'text-gray-400'
                }`}>
                  {step}
                </span>
              </div>
            );
          })}
        </div>

        {/* Estimated Time */}
        <div className="text-center text-gray-600 text-sm">
          預計完成時間: 10-15 秒
        </div>

        {/* Message */}
        <div className="text-center text-gray-500 text-xs mt-3">
          請耐心等待, AI 正在為您生成高質量的練習題
        </div>
      </div>
    </div>
  );
};

export default AiDrillProgressModal; 